import queryString from 'query-string';
import get from 'lodash/get';

import fetchQuery from 'rebook/apps/utils/fetchQuery';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import cropStorageImage from 'rebook/apps/utils/urls/cropStorageImage';
import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import truncateChars from 'rebook/apps/utils/text/truncateChars';
import getPageNumberString from 'rebook/apps/utils/text/getPageNumberString';
import {
  BOOKS_BOOK_CITATIONS_SET_DATA,
  BOOKS_BOOK_CITATIONS_RESET_DATA,
} from 'rebook/apps/books/constants';
import { Book } from 'rebook/apps/books/models';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const { ssr, bookCitationList: currentState } = getState();
    const pageNumber = ssr.query && ssr.query.page
      ? parseInt(ssr.query.page, 10)
      : 1;

    const { bookSlug } = match.params;

    let newState = {
      bookSlug,
    };

    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.citations) {
      newState = {
        ...newState,
        ...currentState,
      };
    }

    // set partial state (it could probably be the data obtained from router)
    dispatch(setData(newState));

    if (!newState.book) {
      await dispatch(fetchBook(match));
    }
    if (!newState.citations || currentState.pageNumber !== pageNumber) {
      await dispatch(fetchCitationList(pageNumber));
    }
  };
}

export function fetchBook(match) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const { bookSlug } = match.params;
    const url = `/api/books/s-${bookSlug}/`;
    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({ book: data }));
        }
      )
      .catch(
        ({ data, resp, error }) => {
          if (resp.status === 404) {
            dispatch(Error404Actions.show());
          }
          else {
            Raven.captureMessage('failed to load citation list book due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load citation list book detail due to server error');
          }
        }
      );
  };
}

const sortCitations = (citations) => {
  if (!citations.length) {
    return citations;
  }
  let result = [];

  for (let i = 0; i < citations.length; i++) {
    const isUserCitation = get(citations[i], 'my_citation', false);
    if (isUserCitation) {
      result.unshift(citations[i]);
    }
    else {
      result.push(citations[i]);
    }
  }

  return result;
};

export function fetchCitationList(pageNumber = 1) {
  return async(dispatch, getState) => {
    const { bookCitationList, ssr } = getState();
    const { book } = bookCitationList;
    if (!book) {
      return;
    }
    // optimistically update the UI
    dispatch(setData({
      citations: null,
      pageNumber,
    }));
    const limit = 10;
    let queryParams = {
      book: book.id,
      limit,
      offset: (pageNumber - 1) * limit,
    };
    const qs = queryString.stringify(queryParams);
    const url = `/api/citations/?${qs}`;
    return await fetchQuery(url, { headers: ssr.headers })
      .then(
        ({ data }) => {
          const lastPageNumber = Math.ceil(data.meta.total_count / limit);
          dispatch(setData({
            citations: sortCitations(data.objects),
            pageNumber,
            lastPageNumber,
          }));
        }
      )
      .catch(
        ({ data, resp, error }) => {
          Raven.captureMessage('failed to load citation list due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          throw new Error('failed to load citation list detail due to server error');
        }
      );
  };
}

function setSeoMeta() {
  return (dispatch, getState) => {
    const { bookCitationList } = getState();
    const { book: bookObject, citations, pageNumber } = bookCitationList;
    const book = new Book(bookObject);
    const cover200x300 = cropStorageImage(book.default_cover, 200, 300);
    const desktopUrl = `https://${BASE_HOST}${book.getCitationsUrl()}`;
    const bookUrl = `https://${BASE_HOST}${book.absolute_url}`;
    const title = `Лучшие цитаты из книги ` +
      `${book.main_author ? `— ${book.main_author.genitive_full_name || book.main_author.cover_name}` : '' } ` +
      `«${book.name}» афоризмы, высказывания и «крылатые фразы» — MyBook. ${getPageNumberString(pageNumber)}`.trim();
    const description = `Читайте интересные цитаты из книги «${book.name}»` +
      `${book.main_author ? ` автора ${book.main_author.cover_name}` : '' }. ` +
      `${book.citations_count > 0 ? `Более ${book.citations_count} цитат и афоризмов, высказываний и «крылатых» фраз `+
      `из книги в электронной библиотеке MyBook.ru.` : ''}`;

    const ogTitle = `Лучшие цитаты из книги «${book.name}»` +
                    `${book.main_author ? `— ${book.main_author.cover_name}` : '' } — MyBook`;
    const ogDescription = truncateChars(`${book.annotation_plain ? ` ${book.annotation_plain}` : ''}`,
      300
    );
    const ogTags = [
      { name: 'type', value: 'books.book' },
      { name: 'image', value: cover200x300 },
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'url', value: desktopUrl },
    ];
    const ogTwitterTags = [
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'image', value: cover200x300 },
    ];
    let ogBookTags = [
      { name: 'rating:value', value: book.rating.rating ? book.rating.rating : 0 },
      { name: 'rating:scale', value: 5 },
    ];
    if (book.ean) {
      ogBookTags.push({
        name: 'isbn',
        value: book.ean,
      });
    }
    if (book.main_author) {
      ogBookTags.push({
        name: 'author',
        value: `https://${BASE_HOST}${book.main_author.absolute_url}`,
      });
    }
    dispatch(SeoActions.setData({
      imageSrc: cover200x300,
      canonicalUrl: citations.length ? desktopUrl : bookUrl,
      title,
      description,
      ogTags,
      ogBookTags,
      ogTwitterTags,
      pageNumber,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { citations } = payload;
    dispatch({
      type: BOOKS_BOOK_CITATIONS_SET_DATA,
      payload,
    });
    if (citations) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch(SeoActions.resetData());
    dispatch({
      type: BOOKS_BOOK_CITATIONS_RESET_DATA,
    });
  };
}
